import {
  ActorForeignHistoryError,
  applyActorMigrations,
  compareActorJournalToBundle,
  getBundledActorMigrationTags,
  readActorJournal,
} from "@-/dev.improve.haniverse.lib.database.actor.migration-tags";

/**
 * The device's one way of changing an actor database's schema (see `database.md`, "Device: schema
 * updater"). The same call migrates the signed-out database and a signed-in replica, on native and
 * inside the web leader, at open and after every download.
 *
 * It is a privileged call on the raw engine, never an app statement:
 *
 * - ONE transaction: the database reaches this build's schema or stays exactly where it was. A
 *   crash rolls it back and the next open runs it again;
 * - on a synced replica it is NOT LOGGED. The engine would otherwise upload the DDL (engine facts
 *   5 and 13), and the cloud copy's schema has exactly one writer, the server. Change capture is
 *   turned off for the transaction, restored, and read back;
 * - a database that is ahead of this build (the cloud copy is newer, or an update was rolled back)
 *   is left alone and opens: migrations are additive, so this build ignores what it does not know.
 */

/** The raw engine calls the updater makes. The caller holds the connection exclusively. */
export interface ActorSchemaEngine {
  all(sql: string): Promise<unknown[]>;
  exec(sql: string): Promise<unknown>;
  run(sql: string, ...params: (number | string)[]): Promise<unknown>;
}

export interface ActorSchemaUpdate {
  /** The migrations this call applied, in order. */
  applied: string[];
  /** The journal after the call. */
  journal: string[];
}

/**
 * Change capture could not be turned back on after a migration. Writes on this connection would
 * no longer be logged, so they would never upload: the connection must be closed. A new open
 * turns capture on again by itself.
 */
export class ActorChangeCaptureLostError extends Error {
  override readonly name = "ActorChangeCaptureLostError";
  constructor(cause: unknown) {
    super(
      "The study database stopped recording changes for sync; its connection must be reopened",
      {
        cause,
      },
    );
  }
}

interface CaptureState {
  mode: string;
  table: string | null;
}

const CAPTURE_PRAGMA = "PRAGMA capture_data_changes_conn";
const CAPTURE_ARGUMENT = /^[\w,]+$/;

function readRows(rows: unknown[]): Record<string, unknown>[] {
  return rows.filter(
    (row): row is Record<string, unknown> => typeof row === "object" && row !== null,
  );
}

async function readCaptureState(engine: ActorSchemaEngine): Promise<CaptureState> {
  const [row] = readRows(await engine.all(CAPTURE_PRAGMA));
  const mode = row?.["mode"];
  const table = row?.["table"];
  if (typeof mode !== "string") {
    throw new TypeError("The database engine did not report its change capture mode");
  }
  return { mode, table: typeof table === "string" ? table : null };
}

/** Run `migrate` with change capture off, and prove capture is back before returning. */
async function withoutChangeCapture<T>(
  engine: ActorSchemaEngine,
  migrate: () => Promise<T>,
): Promise<T> {
  const before = await readCaptureState(engine);
  if (before.mode === "off") {
    // A local database (signed out) records nothing, so there is nothing to keep out of a log.
    return migrate();
  }
  const argument = before.table === null ? before.mode : `${before.mode},${before.table}`;
  if (!CAPTURE_ARGUMENT.test(argument)) {
    throw new TypeError(`Unexpected change capture mode: ${argument}`);
  }
  await engine.exec(`${CAPTURE_PRAGMA}('off')`);
  let outcome: { error: unknown; ok: false } | { ok: true; value: T };
  try {
    outcome = { ok: true, value: await migrate() };
  } catch (error) {
    outcome = { error, ok: false };
  }
  try {
    await engine.exec(`${CAPTURE_PRAGMA}('${argument}')`);
    const after = await readCaptureState(engine);
    if (after.mode !== before.mode || after.table !== before.table) {
      throw new Error(`Change capture is ${after.mode}, expected ${before.mode}`);
    }
  } catch (error) {
    // Reported even when the migration failed too: this one decides the connection's fate.
    throw new ActorChangeCaptureLostError(error);
  }
  if (!outcome.ok) {
    throw outcome.error;
  }
  return outcome.value;
}

/**
 * Bring the database up to this build's migrations. Throws `ActorForeignHistoryError` when its
 * history is not this build's, having changed nothing.
 *
 * `migrations` replaces this build's bundle. The web leader passes the bundle of a tab that is
 * newer than its own worker, so mixed app versions across tabs converge on the newest schema.
 *
 * `onBeforeMigrate` runs once, only when a migration is about to be applied, with the journal the
 * database will hold if it succeeds. A synced replica uses it to announce that head BEFORE the
 * migration commits: a head that is too high is safe (the gateway only refuses or migrates the
 * cloud copy), one that is too low could let a download undo the migration.
 */
export async function updateActorSchema(
  engine: ActorSchemaEngine,
  options: {
    migrations?: Readonly<Record<string, string>>;
    onBeforeMigrate?: (journal: readonly string[]) => void;
  } = {},
): Promise<ActorSchemaUpdate> {
  const read = async (sql: string) => readRows(await engine.all(sql));
  const journal = await readActorJournal(read);
  const bundle =
    options.migrations === undefined
      ? getBundledActorMigrationTags()
      : Object.keys(options.migrations).sort();
  const relation = compareActorJournalToBundle(journal, bundle);
  if (relation === "diverged") {
    throw new ActorForeignHistoryError(journal);
  }
  if (relation !== "behind") {
    return { applied: [], journal };
  }
  const applied = await withoutChangeCapture(engine, async () => {
    await engine.exec("BEGIN IMMEDIATE");
    try {
      const pending = await applyActorMigrations(
        read,
        (sql, args) => (args === undefined ? engine.exec(sql) : engine.run(sql, ...args)),
        options.migrations,
      );
      if (pending.length > 0) {
        options.onBeforeMigrate?.(bundle);
      }
      await engine.exec("COMMIT");
      return pending;
    } catch (error) {
      try {
        await engine.exec("ROLLBACK");
      } catch (rollbackError) {
        // oxlint-disable-next-line eslint/preserve-caught-error -- AggregateError.errors keeps rollbackError; cause keeps the migration failure
        throw new AggregateError(
          [error, rollbackError],
          "The study database migration and its rollback both failed",
          { cause: error },
        );
      }
      throw error;
    }
  });
  return { applied, journal: await readActorJournal(read) };
}
